// ── Adapter Supabase do ITeamUsageRepository ──────────────────────
// Única camada que conhece as RPCs de presença/uso da equipe
// (ver supabase/migrations/20260730_user_active_days.sql).
//
// As duas RPCs ainda não estão em database.types.ts (types não
// regenerados). Usamos o mesmo escape `as never` já adotado em
// SupabaseAgendaRepository para RPCs fora dos types gerados — remover
// os casts quando rodar a regeneração.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import type { ITeamUsageRepository, TeamUsageRow } from '@modules/teamUsage';

const requireSupabase = (): TypedSupabaseClient => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase nao inicializado.');
  return supabase;
};

/** Shape cru devolvido por team_usage_report (snake_case do Postgres). */
interface TeamUsageReportRow {
  user_id: string;
  nome: string | null;
  initials: string | null;
  color: string | null;
  role: string | null;
  first_access: boolean | null;
  ultimo_acesso: string | null;
  primeiro_acesso_hoje: string | null;
  dias_ativos: number | null;
  pings_total: number | null;
  leads_criados: number | null;
  cards_ativos: number | null;
  mudancas_etapa: number | null;
  acoes_total: number | null;
  reunioes: number | null;
  vendas: number | null;
}

const toRow = (r: TeamUsageReportRow): TeamUsageRow => ({
  userId: r.user_id,
  nome: r.nome ?? '—',
  initials: r.initials ?? '?',
  color: r.color,
  role: r.role ?? '',
  // Ausência de perfil não pode virar "nunca entrou" (mostraria alerta falso).
  firstAccess: r.first_access === true,
  ultimoAcesso: r.ultimo_acesso,
  primeiroAcessoHoje: r.primeiro_acesso_hoje,
  diasAtivos: Number(r.dias_ativos ?? 0),
  pingsTotal: Number(r.pings_total ?? 0),
  leadsCriados: Number(r.leads_criados ?? 0),
  cardsAtivos: Number(r.cards_ativos ?? 0),
  mudancasEtapa: Number(r.mudancas_etapa ?? 0),
  acoesTotal: Number(r.acoes_total ?? 0),
  reunioes: Number(r.reunioes ?? 0),
  vendas: Number(r.vendas ?? 0),
});

export class SupabaseTeamUsageRepository implements ITeamUsageRepository {
  touchActiveDay: ITeamUsageRepository['touchActiveDay'] = async (workspaceId) => {
    const supabase = requireSupabase();
    const { error } = await supabase.rpc('touch_active_day' as never, {
      p_workspace: workspaceId,
    } as never);
    // Telemetria não é função crítica: loga e segue, nunca quebra a sessão.
    if (error) console.warn('[teamUsage] touch_active_day falhou:', error.message);
  };

  getTeamUsage: ITeamUsageRepository['getTeamUsage'] = async (workspaceId, days) => {
    const supabase = requireSupabase();
    // ponytail: sobrepõe as 3 métricas enquanto a RPC corrigida não chega ao
    // Supabase; remover estas 2 consultas depois de publicar a migration.
    const [report, leads, history] = await Promise.all([
      supabase.rpc('team_usage_report' as never, {
        p_workspace: workspaceId,
        p_days: days,
      } as never),
      supabase.from('leads').select('sdr_id').eq('workspace_id', workspaceId).eq('ativo', true),
      supabase.from('history').select('created_by, type').eq('workspace_id', workspaceId).not('created_by', 'is', null),
    ]);
    const { data, error } = report;
    if (error) {
      console.error('[teamUsage] team_usage_report falhou:', error);
      throw error;
    }
    if (leads.error || history.error) throw leads.error || history.error;

    const ativos = new Map<string, number>();
    const mudancas = new Map<string, number>();
    const acoes = new Map<string, number>();
    leads.data.forEach(({ sdr_id }) => {
      if (sdr_id) ativos.set(sdr_id, (ativos.get(sdr_id) ?? 0) + 1);
    });
    history.data.forEach(({ created_by, type }) => {
      if (!created_by) return;
      acoes.set(created_by, (acoes.get(created_by) ?? 0) + 1);
      if (type === 'stage_changed') mudancas.set(created_by, (mudancas.get(created_by) ?? 0) + 1);
    });

    return ((data ?? []) as unknown as TeamUsageReportRow[]).map(toRow).map(row => ({
      ...row,
      cardsAtivos: ativos.get(row.userId) ?? 0,
      mudancasEtapa: mudancas.get(row.userId) ?? 0,
      acoesTotal: acoes.get(row.userId) ?? 0,
    }));
  };
}
